import { useState } from "react";
import { X, Users, Cake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  useBulkProgram,
  useBulkRun,
  useBulkRosterMutations,
  type BulkRunMemberRow,
} from "@/hooks/useBulkCelebrations";
import { saveBulkPeriodRecommendation } from "@/lib/saveBulkRecommendation";
import { openBulkCelebrationCheckout } from "@/lib/openBulkCelebrationCheckout";
import type { SkuCardData } from "@/components/chat/SkuCard";
import type { BulkCheckoutSeed } from "@/lib/openBulkCelebrationCheckout";
import { useCompanySettings } from "@/hooks/queries";
import { BulkCelebrationRosterPanel } from "@/components/bulk/BulkCelebrationRosterPanel";
import { BULK_OCCASION_TYPES } from "@/lib/bulkCelebrations";

type Props = {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onOpenCheckout: (sku: SkuCardData, seed: BulkCheckoutSeed) => void;
};

export function BulkCelebrationModal({
  open,
  onClose,
  companyId,
  onOpenCheckout,
}: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const programQuery = useBulkProgram(companyId);
  const companyQuery = useCompanySettings(companyId);
  const { data: runBundle, isLoading, refetch } = useBulkRun(
    companyId,
    programQuery.data ?? undefined,
  );
  const { excludeMember } = useBulkRosterMutations(
    companyId,
    runBundle?.period?.period_key,
  );
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const members = runBundle?.members ?? [];
  const run = runBundle?.run;

  const handleApprove = async () => {
    if (!user?.id || !run || !programQuery.data || !runBundle) return;
    setBusy(true);
    try {
      await saveBulkPeriodRecommendation({
        runId: run.id,
        companyId,
        userId: user.id,
        program: programQuery.data,
        members,
        recipientLabel: runBundle.recipientLabel,
        deliveryDate: runBundle.deliveryDate,
        companyTeamSize: companyQuery.data?.team_size ?? null,
      });
      await refetch();
      toast({ title: "Shared dessert approved for this period" });
    } catch (e) {
      toast({
        title: "Couldn't approve",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    if (!run || !runBundle) return;
    setBusy(true);
    try {
      const result = await openBulkCelebrationCheckout(
        run,
        runBundle.recipientLabel,
        members.map((m) => m.celebration_id),
        runBundle.deliveryDate,
      );
      if (result.kind === "error") {
        toast({ title: result.message, variant: "destructive" });
        return;
      }
      onOpenCheckout(result.sku, result.seed);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-labelledby="bulk-modal-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 id="bulk-modal-title" className="text-lg font-semibold text-navy">
              Bulk period — {runBundle?.period?.label ?? "…"}
            </h2>
            <p className="text-sm text-charcoal/60">
              One shared dessert · {members.length}{" "}
              {members.length === 1 ? "person" : "people"}
              {runBundle?.deliveryDate ? ` · delivers ${runBundle.deliveryDate}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-charcoal/5">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {isLoading && <p className="text-sm text-charcoal/60">Loading roster…</p>}
          {runBundle?.deliveryNote && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {runBundle.deliveryNote}
            </p>
          )}

          <ul className="space-y-2">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                onRemove={() => void excludeMember.mutateAsync(m.id)}
              />
            ))}
            {members.length === 0 && !isLoading && (
              <li className="text-sm text-charcoal/55">
                No celebrations in this period yet. Add people from Home or Settings.
              </li>
            )}
          </ul>

          {runBundle?.mergedDietaries?.length ? (
            <p className="text-xs text-charcoal/55">
              Dietary intersection: {runBundle.mergedDietaries.join(", ")}
            </p>
          ) : null}

          {run && runBundle?.period && run.status !== "ordered" && (
            <BulkCelebrationRosterPanel
              companyId={companyId}
              runId={run.id}
              periodStart={runBundle.period.period_start}
              periodEnd={runBundle.period.period_end}
              occasionTypes={
                programQuery.data?.occasion_types?.length
                  ? programQuery.data.occasion_types
                  : [...BULK_OCCASION_TYPES]
              }
              memberCelebrationIds={members.map((m) => m.celebration_id)}
              onAdded={() => void refetch()}
            />
          )}
        </div>

        <div className="flex flex-wrap gap-2 border-t px-5 py-4">
          {!run?.recommendation_id && members.length > 0 && (
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void handleApprove()}>
              <Cake className="mr-1 h-4 w-4" />
              Approve shared dessert
            </Button>
          )}
          {run?.recommendation_id && (
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void handleCheckout()}>
              Checkout — one invoice
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function MemberRow({
  member,
  onRemove,
}: {
  member: BulkRunMemberRow;
  onRemove: () => void;
}) {
  const cel = member.celebration as {
    date?: string;
    type?: string;
    team_members?: { name?: string };
  } | undefined;
  const name = cel?.team_members?.name ?? "Team member";
  return (
    <li className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4 text-charcoal/40" />
        {name}
        <span className="text-charcoal/45">
          {cel?.type?.replace(/_/g, " ")} · {cel?.date}
        </span>
      </span>
      <button
        type="button"
        className="text-xs text-charcoal/50 underline"
        onClick={onRemove}
      >
        Remove
      </button>
    </li>
  );
}
